import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  Filter,
  ListChecks,
  Home,
  LayoutDashboard,
  TrendingUp,
  LineChart,
  SlidersHorizontal,
  Search,
  ScanSearch,
  Target,
  Percent,
  // Newspaper,
  // Briefcase,
  // Bell,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface FeatureDetail {
  name: string;
  description: string;
  icon: LucideIcon;
}

interface FeatureStep {
  id: string;
  number: string;
  title: string;
  tagline: string;
  taglineIcon: LucideIcon;
  features: FeatureDetail[];
  accentText: string;
  accentBg: string;
}

const featureJourney: FeatureStep[] = [
  {
    id: "discover",
    number: "01",
    title: "Discover",
    tagline: "Find companies worth your focus",
    taglineIcon: Search,
    features: [
      {
        name: "Guided text screener",
        description:
          "Filter companies from hundreds of filters, without the hassle of dropdown mazes.",
        icon: Filter,
      },
      {
        name: "Your market feed, without the noise",
        description:
          "News on the companies you've saved,kept close, stripped of the noise.",
        icon: Home,
      },
      {
        name: "Your universe, not someone else's",
        description:
          "Discover companies that fit your style, not someone else's.",
        icon: SlidersHorizontal,
      },      
    ],
    accentText: "text-emerald-100",
    accentBg: "bg-emerald-700",
  },
  {
    id: "evaluate",
    number: "02",
    title: "Evaluate",
    tagline: "Glance & deep dive what you found",
    taglineIcon: ScanSearch,
    features: [
      {
        name: "Ticker Breakdowns",
        description:
          "One clean breakdown covering the business and its fundamentals.",
        icon: LayoutDashboard,
      },
      {
        name: "Visualize financials",
        description:
          "Charts and snapshots for the numbers that matter, so the story behind the screen is clear.",
        icon: TrendingUp,
      },
      {
        name: "Evaluation criteria",
        description:
          "Your investing checklist, applied the same way to every company you study.",
        icon: ListChecks,
      },
    ],
    accentText: "text-emerald-100",
    accentBg: "bg-emerald-700",
  },
  {
    id: "model",
    number: "03",
    title: "Forecast",
    tagline: "Estimate returns based on assumptions",
    taglineIcon: Target,
    features: [
      {
        name: "Visualize price projections",
        description:
          "Set your assumptions and walk bull, base, and bear paths from the research you just did.",
        icon: LineChart,
      },
      {
        name: "Growth & margin drivers",
        description:
          "Dial revenue growth, margins, and exit multiples for each scenario.",
        icon: SlidersHorizontal,
      },
      {
        name: "Implied returns",
        description:
          "See the CAGR and target price each path implies, so conviction has a clear payoff.",
        icon: Percent,
      },
    ],
    accentText: "text-emerald-100",
    accentBg: "bg-emerald-700",
  },
  // {
  //   id: "track",
  //   number: "04",
  //   title: "Track",
  //   tagline: "Stay current on companies you own",
  //   features: [
  //     {
  //       name: "Research Digest",
  //       description: "Weekly deep research on stocks you hold.",
  //       icon: Newspaper,
  //     },
  //     {
  //       name: "Holdings Overview",
  //       description: "See portfolio updates at a glance.",
  //       icon: Briefcase,
  //     },
  //     {
  //       name: "New Signals",
  //       description: "High-signal changes worth your attention.",
  //       icon: Bell,
  //     },
  //   ],
  //   accentText: "text-emerald-100",
  //   accentBg: "bg-emerald-700",
  // },
];

const WOOD_FRAME_BG =
  "linear-gradient(145deg, #f0e2c4 0%, #d4b896 28%, #b8956a 55%, #c9a87a 78%, #e6d4b0 100%)";

const WOOD_FRAME_SHADOW =
  "0 1px 0 rgba(255,245,220,0.85) inset, 0 -1px 0 rgba(120,80,40,0.18) inset, 0 22px 50px -28px rgba(90,60,30,0.28), 0 8px 20px -12px rgba(90,60,30,0.12)";

/** Animated progress segment between two pillar cards. */
const PillarConnector: React.FC<{
  orientation: "horizontal" | "vertical";
  delay?: number;
  className?: string;
}> = ({ orientation, delay = 0, className = "" }) => {
  const reducedMotion = useReducedMotion();
  const isHorizontal = orientation === "horizontal";

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute z-10 flex items-center justify-center ${className}`}
    >
      <div
        className={`relative overflow-hidden rounded-full ${
          isHorizontal ? "h-[4.5px] w-full" : "h-full w-[4.5px]"
        }`}
        style={{ background: "rgba(8, 122, 75, 0.12)" }}
      >
        <motion.div
          className={`absolute rounded-full ${
            isHorizontal
              ? "inset-y-0 left-0 origin-left"
              : "inset-x-0 top-0 origin-top"
          }`}
          style={{
            background:
              "linear-gradient(90deg, #34d399 0%, #087a4b 55%, #047857 100%)",
            boxShadow: "0 0 10px rgba(8, 122, 75, 0.4)",
            ...(isHorizontal ? { width: "100%" } : { height: "100%" }),
          }}
          initial={
            reducedMotion
              ? false
              : isHorizontal
                ? { scaleX: 0 }
                : { scaleY: 0 }
          }
          whileInView={isHorizontal ? { scaleX: 1 } : { scaleY: 1 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : {
                  duration: 0.9,
                  delay,
                  ease: [0.21, 0.65, 0.3, 1],
                }
          }
        >
          {!reducedMotion && (
            <motion.span
              className={`absolute bg-gradient-to-r from-transparent via-white/55 to-transparent ${
                isHorizontal ? "inset-y-0 w-1/2" : "inset-x-0 h-1/2"
              }`}
              animate={
                isHorizontal
                  ? { x: ["-120%", "220%"] }
                  : { y: ["-120%", "220%"] }
              }
              transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: "linear",
                delay: delay + 0.9,
              }}
            />
          )}
        </motion.div>
      </div>
      {/* Soft rounded thread tips */}
      <span
        className="absolute size-[9px] rounded-full bg-[#087a4b] shadow-[0_0_0_2px_rgba(8,122,75,0.12)]"
        style={isHorizontal ? { left: -1 } : { top: -1 }}
      />
      <span
        className="absolute size-[9px] rounded-full bg-[#087a4b] shadow-[0_0_0_2px_rgba(8,122,75,0.12)]"
        style={isHorizontal ? { right: -1 } : { bottom: -1 }}
      />
    </div>
  );
};

const PillarCardBody: React.FC<{ step: FeatureStep }> = ({ step }) => {
  const TaglineIcon = step.taglineIcon;

  return (
  <>
    <div className="relative flex items-center justify-center gap-4 p-7 pb-4 md:p-9 md:pb-5">
      <span
        aria-hidden
        className="shrink-0 select-none text-5xl font-bold leading-none tracking-[-0.04em] text-yellow-600/40 tabular-nums md:text-6xl"
      >
        {step.number}
      </span>
      <h3 className="text-3xl font-extrabold leading-tight text-black md:text-4xl">
        {step.title}
      </h3>
    </div>

    <div className="relative mx-7 mb-7 flex justify-center md:mx-9 md:mb-8">
      <div
        className="inline-flex max-w-full items-center gap-2 rounded-full px-3.5 py-2"
        style={{
          background: "#edd78f",
          boxShadow:
            "0 2px 8px rgba(200, 165, 80, 0.22), 0 0 0 1px rgba(5,150,105,0.18)",
        }}
      >
        <TaglineIcon
          aria-hidden
          className="h-4 w-4 shrink-0 text-[#087a4b]"
          strokeWidth={2}
        />
        <p className="whitespace-nowrap text-[0.8rem] font-semibold leading-none tracking-[-0.01em] text-[#2a1f0e] md:text-[0.85rem]">
          {step.tagline}
        </p>
      </div>
    </div>

    <ul className="relative flex flex-1 flex-col gap-5 px-6 pb-8 md:gap-5 md:px-8 md:pb-9">
      {step.features.map((feature) => {
        const FeatureIcon = feature.icon;
        return (
          <li key={feature.name} className="flex min-w-0 items-start gap-3">
            <div
              className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${step.accentBg} ${step.accentText}`}
            >
              <FeatureIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[0.95rem] font-semibold leading-snug text-black">
                {feature.name}
              </p>
              <p className="mt-0.5 text-[0.8rem] leading-relaxed text-black/45">
                {feature.description}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  </>
  );
};

/** Mobile tab rail — clean strip attached to the top of the wooden frame. */
const MobileFeatureTabs: React.FC = () => {
  const [activeId, setActiveId] = useState(featureJourney[0].id);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useReducedMotion();
  const activeStep =
    featureJourney.find((s) => s.id === activeId) ?? featureJourney[0];

  useEffect(() => {
    if (reducedMotion || paused) return;

    const timer = window.setInterval(() => {
      setActiveId((current) => {
        const index = featureJourney.findIndex((s) => s.id === current);
        const next = (index + 1) % featureJourney.length;
        return featureJourney[next].id;
      });
    }, 7000);

    return () => window.clearInterval(timer);
  }, [reducedMotion, paused, activeId]);

  return (
    <div
      className="md:hidden"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div
        className="rounded-[22px] p-[6px]"
        style={{
          background: WOOD_FRAME_BG,
          boxShadow: WOOD_FRAME_SHADOW,
        }}
      >
        <div
          className="overflow-hidden rounded-[16px] bg-white"
          style={{
            boxShadow:
              "0 0 0 1px rgba(120,80,40,0.12) inset, 0 1px 0 rgba(255,255,255,0.95) inset",
          }}
        >
          {/* Tab strip — flush to the top of the wood frame */}
          <div
            role="tablist"
            aria-label="Research workflow pillars"
            className="flex border-b border-black/[0.1]"
            style={{
              background: "#ebe6dc",
              boxShadow: "0 1px 0 rgba(255,255,255,0.55) inset",
            }}
          >
            {featureJourney.map((step) => {
              const isActive = step.id === activeId;
              return (
                <button
                  key={step.id}
                  type="button"
                  role="tab"
                  id={`feature-tab-${step.id}`}
                  aria-selected={isActive}
                  aria-controls={`feature-panel-${step.id}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveId(step.id)}
                  className={`relative min-w-0 flex-1 px-1.5 py-3.5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#087a4b]/35 ${
                    isActive
                      ? "rounded-t-xl bg-white text-black shadow-[0_1px_0_0_white]"
                      : "bg-transparent text-black/30 hover:text-black/45"
                  }`}
                >
                  <span className="flex flex-col items-center gap-0.5">
                    <span
                      className={`text-[0.65rem] font-bold tabular-nums tracking-wide ${
                        isActive ? "text-[#087a4b]" : "text-black/25"
                      }`}
                    >
                      {step.number}
                    </span>
                    <span
                      className={`truncate text-[0.85rem] font-extrabold leading-tight ${
                        isActive ? "text-black" : "text-black/30"
                      }`}
                    >
                      {step.title}
                    </span>
                  </span>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute inset-x-2.5 bottom-0 h-[2.5px] rounded-full bg-[#087a4b]"
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            id={`feature-panel-${activeStep.id}`}
            aria-labelledby={`feature-tab-${activeStep.id}`}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep.id}
                initial={reducedMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reducedMotion ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.28, ease: [0.21, 0.65, 0.3, 1] }}
                className="flex flex-col"
              >
                <PillarCardBody step={activeStep} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  const reducedMotion = useReducedMotion();
  const wobbleDuration = 1.45;
  const pillarCount = featureJourney.length;

  return (
    <section
      id="features"
      className="relative w-full scroll-mt-8 overflow-hidden py-20 md:py-32 lg:scroll-mt-0"
      style={{
        background: "#f6f5f0",
        maxWidth: "100vw",
        width: "100%",
      }}
    >
      <div className="relative mx-auto max-w-[1400px] px-2 md:px-6">
        <motion.div
          className="relative mb-12 flex flex-col items-center text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.65, ease: [0.21, 0.65, 0.3, 1] }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-8 h-28 w-60 rounded-full blur-3xl md:h-36 md:w-[22rem]"
          />
          <span
            className="relative inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.2em] backdrop-blur"
            style={{
              background: "#edd78f",
              color: "#3a3018",
              letterSpacing: "0.2em",
              boxShadow:
                "0 2px 8px rgba(200, 165, 80, 0.22), 0 0 0 1px rgba(5,150,105,0.18)",
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 shadow-[0_0_0_3px_rgba(5,150,105,0.2)]" />
            The Research Flow
          </span>
          <h2 className="relative mt-5 max-w-3xl text-balance text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-black md:text-6xl">
            Turn noise into high conviction decisions.
          </h2>
        </motion.div>

        {/* Mobile: wooden-edged tabs */}
        <MobileFeatureTabs />

        {/* Tablet / desktop: connected pillar cards */}
        <div className="relative hidden grid-cols-1 gap-5 md:grid md:grid-cols-2 md:gap-7 xl:grid-cols-3">
          {featureJourney.map((step, index) => {
            const isLast = index === featureJourney.length - 1;
            return (
              <motion.article
                key={step.id}
                className="group relative"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.12,
                  ease: [0.21, 0.65, 0.3, 1],
                }}
              >
                {/* Desktop xl: horizontal connectors between cards */}
                {!isLast && (
                  <PillarConnector
                    orientation="horizontal"
                    delay={0.35 + index * 0.35}
                    className="top-[3.75rem] left-[calc(100%-1.35rem)] hidden w-[calc(1.75rem+2.7rem)] xl:flex"
                  />
                )}
                {/* md 2-col: horizontal connector between first two only */}
                {index === 0 && (
                  <PillarConnector
                    orientation="horizontal"
                    delay={0.35}
                    className="top-[3.75rem] left-[calc(100%-1.35rem)] hidden w-[calc(1.75rem+2.7rem)] md:flex xl:hidden"
                  />
                )}

                {/* Light wood frame around each pillar card */}
                <motion.div
                  className="h-full"
                  animate={reducedMotion ? undefined : { y: [0, -5, 0] }}
                  transition={
                    reducedMotion
                      ? undefined
                      : {
                          duration: wobbleDuration,
                          times: [0, 0.45, 1],
                          ease: ["easeInOut", "easeInOut"],
                          delay: index * wobbleDuration + 0.9,
                          repeat: Infinity,
                          repeatDelay: (pillarCount - 1) * wobbleDuration,
                        }
                  }
                >
                  <div
                    className="h-full rounded-[24px] p-[6px] transition-transform duration-500 group-hover:-translate-y-1"
                    style={{
                      background: WOOD_FRAME_BG,
                      boxShadow: WOOD_FRAME_SHADOW,
                    }}
                  >
                    <div
                      className="relative flex h-full flex-col overflow-hidden rounded-[17px] bg-white"
                      style={{
                        boxShadow:
                          "0 0 0 1px rgba(120,80,40,0.12) inset, 0 1px 0 rgba(255,255,255,0.95) inset",
                      }}
                    >
                      <PillarCardBody step={step} />
                    </div>
                  </div>
                </motion.div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
